import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Check, ArrowRight, Sparkles } from "lucide-react"
import { useState, useMemo } from "react"
import MarketingLayout from "@/components/marketing/MarketingLayout"
import { Button } from "@/components/ui/button"
import { buildPricingPageJsonLd } from "@/components/structured-data"
import { LOCALE_CONFIG } from "@/config/locale-domains"
import { useBrand } from "@/hooks/use-brand"
import { isEnabled } from "@/lib/feature-flags"
import { formatRon } from "@/lib/format"

const PRICES = {
  Solo: { monthly: 39, yearly: 32 },
  Practice: { monthly: 79, yearly: 65 },
  Clinic: { monthly: 149, yearly: 125 },
}

const POPULAR_PLAN = "Practice"

function isPopularPlan(name) {
  return name === POPULAR_PLAN
}

function shouldShowPlanFeature(label) {
  if (typeof label !== "string" || !label) return false
  if (/telemedicin/i.test(label) && !isEnabled("telehealthHd")) return false
  if (/(ședințe de grup|csoportos session)/i.test(label) && !isEnabled("telehealthGroupSessions")) return false
  if (/stripe/i.test(label) && !isEnabled("stripeBilling")) return false
  return true
}

export default function Pricing() {
  const { t } = useTranslation()
  const brand = useBrand()
  const domain = LOCALE_CONFIG[brand.locale]?.domain || LOCALE_CONFIG.ro.domain
  const [billing, setBilling] = useState("monthly")

  const plans = t("pricing.plans", { returnObjects: true, defaultValue: [] })
  const planList = Array.isArray(plans) ? plans : []
  const faqs = t("pricing.faqs", { returnObjects: true, defaultValue: [] })
  const faqList = Array.isArray(faqs) ? faqs : []

  const jsonLd = useMemo(
    () =>
      buildPricingPageJsonLd({
        domain,
        faqs: faqList,
        plans: planList,
        billing,
        priceTable: PRICES,
      }),
    [domain, faqList, planList, billing]
  )

  return (
    <MarketingLayout seoKey="pricing" jsonLd={jsonLd}>
      <section className="pt-36 pb-12 text-center">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-5">
            {t("pricing.eyebrow")}
          </p>
          <h1 className="font-display text-5xl md:text-6xl tracking-tighter font-medium leading-[0.95]">
            {t("pricing.title")}
          </h1>
          <p className="mt-6 text-muted-foreground">{t("pricing.subtitle")}</p>

          <div className="mt-10 inline-flex p-1 rounded-full border border-border bg-card" data-testid="billing-toggle">
            {["monthly", "yearly"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setBilling(m)}
                data-testid={`billing-${m}`}
                className={`px-5 py-1.5 rounded-full text-sm transition-colors ${
                  billing === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {m === "monthly" ? t("pricing.monthly") : `${t("pricing.yearly")} · ${t("pricing.save")}`}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid md:grid-cols-3 gap-6">
          {planList.map((p) => {
            const prices = PRICES[p.name] || { monthly: 0, yearly: 0 }
            const amount = prices[billing === "yearly" ? "yearly" : "monthly"]
            const visibleFeatures = (p.features || []).filter(shouldShowPlanFeature)
            const highlighted = isPopularPlan(p.name)
            return (
              <div
                key={p.name}
                data-testid={`plan-${p.name.toLowerCase()}`}
                className={`relative rounded-2xl border bg-card p-8 flex flex-col ${
                  highlighted ? "border-foreground/30 beam" : "border-border"
                }`}
              >
                {highlighted && (
                  <span className="absolute top-4 right-4 text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 rounded-full bg-accent text-accent-foreground inline-flex items-center gap-1 font-semibold">
                    <Sparkles strokeWidth={1.5} className="h-3 w-3" /> {t("pricing.popular")}
                  </span>
                )}
                <p className="font-display text-2xl tracking-tight font-medium">{p.name}</p>
                <p className="text-sm text-muted-foreground mt-1">{p.tagline}</p>
                <div className="mt-6 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="font-display text-5xl tracking-tighter font-medium">
                    {formatRon(amount, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </span>
                  <span className="text-muted-foreground text-sm">
                    {t("pricing.perMonth")} {t("pricing.perPractitioner")}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {billing === "yearly" ? t("pricing.billedYearly") : t("pricing.billedMonthly")}
                </p>

                <ul className="mt-8 space-y-3 text-sm flex-1">
                  {visibleFeatures.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <Check strokeWidth={2} className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link to={`/signup?plan=${encodeURIComponent(p.name.toLowerCase())}&interval=${billing}`} className="mt-8 block">
                  <Button
                    size="lg"
                    variant={highlighted ? "default" : "outline"}
                    className="w-full rounded-full"
                    data-testid={`plan-cta-${p.name.toLowerCase()}`}
                  >
                    {p.cta} <ArrowRight strokeWidth={1.5} className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            )
          })}
        </div>
        {planList.length > 0 ? (
          <p className="mx-auto max-w-7xl px-6 lg:px-10 mt-8 text-xs text-muted-foreground text-center">{t("pricing.currencyNote")}</p>
        ) : null}
      </section>

      {(() => {
        const addons = t("pricing.addons", { returnObjects: true, defaultValue: [] })
        // Hide predictive_ai until a real model ships (API returns empty predictive {}).
        const addonList = (Array.isArray(addons) ? addons : []).filter((a) => a?.id !== "predictive_ai")
        if (!addonList.length) return null
        return (
          <section className="border-t border-border py-16">
            <div className="mx-auto max-w-4xl px-6 lg:px-10">
              <h2 className="font-display text-2xl tracking-tight font-medium mb-2">{t("pricing.addonsTitle")}</h2>
              <p className="text-sm text-muted-foreground mb-8">Disponibile cu planul Clinic.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {addonList.map((a) => (
                  <div key={a.id} className="rounded-xl border border-border p-5 bg-card">
                    <p className="font-medium text-sm">{a.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{a.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )
      })()}

      <section className="border-t border-border py-24" id="faq">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <h2 className="font-display text-3xl md:text-4xl tracking-tight font-medium mb-10">{t("pricing.faqTitle")}</h2>
          <div className="divide-y divide-border">
            {faqList.map(([q, a]) => (
              <div key={q} className="py-6">
                <p className="font-display text-lg tracking-tight font-medium">{q}</p>
                <p className="text-sm text-muted-foreground mt-2">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </MarketingLayout>
  )
}
